// djin-cloud/web/src/components/site-detail/tabs/MonthlySummaryTab.tsx

import { useState } from 'react';
import { GroupedBarChart } from '../../charts/echarts/GroupedBarChart';
import { useEnergySummary } from '../../../hooks/useEnergySummary';

interface KPICardProps {
  label: string;
  value: string | number;
  unit: string;
  color?: string;
}

function KPICard({ label, value, unit, color }: KPICardProps) {
  return (
    <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-4">
      <div className="text-[10px] uppercase tracking-wider text-ems-text-muted font-medium">
        {label}
      </div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className={`text-xl font-bold font-mono ${color ?? 'text-ems-text-primary'}`}>
          {value}
        </span>
        <span className="text-xs text-ems-text-muted">{unit}</span>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-2xl bg-ems-bg-card border border-ems-border p-4 h-20" />
        ))}
      </div>
      <div className="rounded-2xl bg-ems-bg-card border border-ems-border h-80" />
    </div>
  );
}

interface MonthlySummaryTabProps {
  siteId: string;
}

export function MonthlySummaryTab({ siteId }: MonthlySummaryTabProps) {
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  // Default to current month in YYYY-MM format
  const defaultMonth = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);

  const { data, isLoading, isError } = useEnergySummary(siteId, period, selectedMonth);

  const summary = data?.summary;
  const breakdown = (data?.data ?? []).map((item) => ({
    label: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    consumption: item.consumption_kwh,
    generation: item.generation_kwh,
    bess_throughput: item.bess_throughput_kwh,
  }));

  return (
    <div className="p-6 bg-ems-bg min-h-full space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KPICard
          label="Total Consumption"
          value={isLoading ? '—' : (summary?.total_consumption_kwh?.toFixed(1) ?? '—')}
          unit="kWh"
          color="text-ems-text-primary"
        />
        <KPICard
          label="Total Generation"
          value={isLoading ? '—' : (summary?.total_generation_kwh?.toFixed(1) ?? '—')}
          unit="kWh"
          color="text-ems-green"
        />
        <KPICard
          label="Peak Demand"
          value={isLoading ? '—' : (summary?.peak_demand_kw?.toFixed(1) ?? '—')}
          unit="kW"
          color="text-ems-text-primary"
        />
        <KPICard
          label="Avg Load"
          value={isLoading ? '—' : (summary?.avg_load_kw?.toFixed(1) ?? '—')}
          unit="kW"
          color="text-ems-text-primary"
        />
        <KPICard
          label="Renewable Mix"
          value={isLoading ? '—' : (summary?.renewable_mix_pct?.toFixed(1) ?? '—')}
          unit="%"
          color="text-ems-green"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Periodicity toggle */}
        <div className="flex items-center gap-1 rounded-xl bg-ems-bg-card border border-ems-border p-1">
          {(['daily', 'weekly', 'monthly'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-ems-green/20 text-ems-green'
                  : 'text-ems-text-muted hover:text-ems-text-secondary'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>

        {/* Month picker */}
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="rounded-lg bg-ems-bg-card border border-ems-border px-3 py-2 text-sm text-ems-text-primary font-mono focus:outline-none focus:ring-1 focus:ring-ems-green/40"
        />
      </div>

      {/* Chart area */}
      <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-6">
        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <div className="flex items-center justify-center h-64 text-ems-text-muted text-sm">
            Failed to load energy summary data.
          </div>
        ) : breakdown.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-ems-text-muted text-sm">
            No data available for the selected period.
          </div>
        ) : (
          <GroupedBarChart data={breakdown} height={320} />
        )}
      </div>
    </div>
  );
}
